import { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '../components/Auth/AuthProvider';
import { apiClient } from '../lib/api';

/**
 * Table State Configuration
 * Defines the shape of user preferences for table views
 */
export interface TableStateConfig {
  columnVisibility?: Record<string, boolean>;
  columnOrder?: string[];
  columnSizing?: Record<string, number>;
  sorting?: Array<{ id: string; desc: boolean }>;
  globalFilter?: string;
  columnFilters?: Array<{ id: string; value: any }>;
  pagination?: { pageIndex: number; pageSize: number };
  density?: 'comfortable' | 'compact' | 'spacious';
  showColumnFilters?: boolean;
  [key: string]: any; // Allow additional custom properties
}

export interface UseTableStatePersistenceOptions {
  reportId: string;
  localStoragePrefix: string;
  debounceMs?: number;
  defaultState?: Partial<TableStateConfig>;
}

export interface UseTableStatePersistenceReturn {
  state: TableStateConfig;
  updateState: <K extends keyof TableStateConfig>(key: K, value: TableStateConfig[K]) => void;
  updateMultipleState: (updates: Partial<TableStateConfig>) => void;
  isLoaded: boolean;
}

/**
 * Hook to persist table state (column visibility, filters, sorting, etc.) to API and localStorage
 * Uses React Query for caching and efficient API calls
 */
export function useTableStatePersistence({
  reportId,
  localStoragePrefix,
  debounceMs = 600,
  defaultState = {},
}: UseTableStatePersistenceOptions): UseTableStatePersistenceReturn {
  const { user } = useAuth();
  const userId = useMemo(() => {
    try {
      return localStorage.getItem('afl_user_id') || `anon_${Date.now()}`;
    } catch {
      return `anon_${Date.now()}`;
    }
  }, []);

  // Track if state has been modified by user
  const [isDirty, setIsDirty] = useState(false);

  // Local state for table configuration
  const [state, setState] = useState<TableStateConfig>(() => {
    // Try loading from localStorage first for instant UI
    const initialState: TableStateConfig = { ...defaultState };
    try {
      Object.keys(defaultState).forEach((key) => {
        const localKey = `${localStoragePrefix}_${key}`;
        const storedValue = localStorage.getItem(localKey);
        if (storedValue) {
          try {
            initialState[key] = JSON.parse(storedValue);
          } catch {
            initialState[key] = storedValue;
          }
        }
      });
    } catch (error) {
      console.warn('Failed to load state from localStorage:', error);
    }

    return initialState;
  });

  // Query: Load state from API (cached for 5 minutes)
  const { data: apiState, isSuccess } = useQuery({
    queryKey: ['table-state', reportId, userId],
    queryFn: async () => {
      try {
        const base = (import.meta.env.VITE_API_URL as string) || window.location.origin;
        let response: Response;

        if (user) {
          // Use authenticated endpoint
          response = await apiClient.get(`/api/prefs/report/authenticated?reportId=${reportId}`);
        } else {
          // Use anonymous endpoint with x-user-id header
          const prefsUrl = new URL(`/api/prefs/report?reportId=${reportId}`, base).toString();
          const tenantSlug =
            localStorage.getItem('selected_tenant_slug') ||
            (import.meta.env.VITE_TENANT_SLUG as string) ||
            'ourisman-va_alexandria-ford';

          response = await fetch(prefsUrl, {
            headers: {
              'x-user-id': userId,
              'x-tenant-slug': tenantSlug,
            },
          });
        }

        if (response.ok) {
          const data = await response.json();
          return data.settings || {};
        }
        return {};
      } catch (error) {
        console.warn('Failed to load state from API:', error);
        return {};
      }
    },
    staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes
    retry: 1,
  });

  // Mutation: Save state to API
  const saveStateMutation = useMutation({
    mutationFn: async (stateToSave: TableStateConfig) => {
      const base = (import.meta.env.VITE_API_URL as string) || window.location.origin;

      if (user) {
        // Use authenticated endpoint - apiClient already handles JSON.stringify
        await apiClient.post(`/api/prefs/report/authenticated?reportId=${reportId}`, {
          settings: stateToSave,
        });
      } else {
        // Use anonymous endpoint with x-user-id header
        const prefsUrl = new URL(`/api/prefs/report?reportId=${reportId}`, base).toString();
        const tenantSlug =
          localStorage.getItem('selected_tenant_slug') ||
          (import.meta.env.VITE_TENANT_SLUG as string) ||
          'ourisman-va_alexandria-ford';

        await fetch(prefsUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-user-id': userId,
            'x-tenant-slug': tenantSlug,
          },
          body: JSON.stringify({ settings: stateToSave }),
        });
      }

      // Also save to localStorage as backup
      Object.entries(stateToSave).forEach(([key, value]) => {
        const localKey = `${localStoragePrefix}_${key}`;
        try {
          if (typeof value === 'string') {
            localStorage.setItem(localKey, value);
          } else {
            localStorage.setItem(localKey, JSON.stringify(value));
          }
        } catch {
          // Ignore localStorage errors
        }
      });
    },
    retry: 1,
  });

  // Update state from API when loaded
  useEffect(() => {
    if (isSuccess && apiState && Object.keys(apiState).length > 0) {
      setState((prevState) => ({
        ...prevState,
        ...apiState,
      }));

      // Sync latest API settings to localStorage backup
      Object.entries(apiState).forEach(([key, value]) => {
        const localKey = `${localStoragePrefix}_${key}`;
        try {
          if (typeof value === 'string') {
            localStorage.setItem(localKey, value);
          } else {
            localStorage.setItem(localKey, JSON.stringify(value));
          }
        } catch {
          // Ignore storage errors
        }
      });

      // Reset dirty flag after loading from API
      setIsDirty(false);
    }
  }, [apiState, isSuccess, localStoragePrefix]);

  // Debounced save to API - only if user has made changes
  useEffect(() => {
    if (!isSuccess || !isDirty) return; // Don't save until loaded AND user has made changes

    const timeoutId = setTimeout(() => {
      saveStateMutation.mutate(state);
      setIsDirty(false); // Reset dirty flag after save
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [state, isSuccess, isDirty, debounceMs]);

  // Helper function to update specific state properties
  const updateState = useCallback(
    <K extends keyof TableStateConfig>(key: K, value: TableStateConfig[K]) => {
      setState((prevState) => ({ ...prevState, [key]: value }));
      setIsDirty(true); // Mark as dirty when user makes changes
    },
    [],
  );

  // Helper function to update multiple state properties
  const updateMultipleState = useCallback((updates: Partial<TableStateConfig>) => {
    setState((prevState) => ({ ...prevState, ...updates }));
    setIsDirty(true); // Mark as dirty when user makes changes
  }, []);

  return {
    state,
    updateState,
    updateMultipleState,
    isLoaded: isSuccess,
  };
}
